import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { apiService } from '../../services/apiService';
import OrderSubmissionForm from './OrderSubmissionForm';
import './OrderBook.css';

interface OrderBookEntry {
  price: string;
  quantity: string;
  providers?: string[];
}

interface OrderBookResponse {
  symbol: string;
  provider: string;
  isMockData: boolean;
  dataSource: string;
  timestamp: number;
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  bestBid: string;
  bestAsk: string;
  spread: string;
  spreadPercentage: string;
  internalBids?: OrderBookEntry[];
  internalAsks?: OrderBookEntry[];
  hasInternalOrders?: boolean;
  internalOrderCount?: number;
}

interface OrderBookProps {
  symbol?: string;
  provider?: string;
  depth?: number;
  onSymbolChange?: (symbol: string) => void;
}

/**
 * Order Book Component for Retail Frontend
 * Displays market depth with bids and asks
 */
const OrderBook: React.FC<OrderBookProps> = ({ 
  symbol: initialSymbol = 'BTC-USDT', 
  provider: initialProvider,
  depth: initialDepth = 20,
  onSymbolChange 
}) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [provider, setProvider] = useState(initialProvider || 'aggregated');
  const [depth, setDepth] = useState(initialDepth);
  const [orderBook, setOrderBook] = useState<OrderBookResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false); // Disable auto-refresh by default to prevent issues
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<{ price: number; side: 'BUY' | 'SELL' } | null>(null);
  const [includeInternal, setIncludeInternal] = useState(true); // Include internal orders by default
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef(false); // Guard to prevent multiple simultaneous loads

  const token = useAppSelector(state => state.auth.token);
  const profile = useAppSelector(state => state.profile.profile);

  const supportedSymbols = ['BTC-USDT', 'ETH-USDT', 'DOGE-USDT', 'SHIB-USDT', 'PEPE-USDT', 'FLOKI-USDT', 'BONK-USDT'];
  const depthOptions = [10, 20, 50, 100];

  const loadOrderBook = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }

    if (!token) {
      setError('Not authenticated');
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (provider === 'aggregated') {
        response = await apiService.get(`/api/retail/orderbook/${symbol}/aggregated?depth=${depth}&includeInternal=${includeInternal}`);
      } else {
        response = await apiService.get(`/api/retail/orderbook/${symbol}?provider=${provider}&depth=${depth}&includeInternal=${includeInternal}`);
      }

      // apiService.get returns { success: boolean, data: T }
      // Backend returns StandardApiResponse: { code, status, message, data }
      // So response.data is the StandardApiResponse, and response.data.data is the actual order book
      
      let orderBookData = null;
      
      if (response && response.success && response.data) {
        const apiResponse = response.data;
        
        // Check if it's StandardApiResponse format
        if (apiResponse.status === 'Successful' && apiResponse.code === 200 && apiResponse.data) {
          // StandardApiResponse with nested data
          orderBookData = apiResponse.data;
        } else if (apiResponse.symbol || apiResponse.bids || apiResponse.asks) {
          // Data is directly in response.data (already parsed)
          orderBookData = apiResponse;
        } else {
          throw new Error(apiResponse.message || 'Failed to load order book');
        }
      } else if (response && !response.success) {
        throw new Error(response.error || 'Failed to load order book');
      } else {
        throw new Error('Invalid response format');
      }

      if (orderBookData) {
        setOrderBook(orderBookData);
        setError(null);
      } else {
        throw new Error('No data received from API');
      }
    } catch (err: any) {
      console.error('Error loading order book:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view order book data');
      } else {
        setError(err.message || err.response?.data?.message || 'Failed to load order book');
      }
      // Don't clear orderBook on error to keep showing last successful data
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [token, symbol, provider, depth, includeInternal]);

  // Store latest loadOrderBook in a ref to avoid dependency issues
  const loadOrderBookRef = useRef(loadOrderBook);
  useEffect(() => {
    loadOrderBookRef.current = loadOrderBook;
  }, [loadOrderBook]);

  // Initial load effect - only runs when key parameters change
  useEffect(() => {
    // Load immediately when symbol, provider, depth, or token changes
    if (token && !isLoadingRef.current) {
      loadOrderBookRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, provider, depth, token, includeInternal]); // Include includeInternal to refresh when toggled

  // Auto-refresh effect - separate from initial load
  useEffect(() => {
    // Clear any existing interval first
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Set up auto-refresh interval if enabled
    if (autoRefresh && token) {
      refreshIntervalRef.current = setInterval(() => {
        // Only refresh if not currently loading
        if (!isLoadingRef.current && token) {
          loadOrderBookRef.current();
        }
      }, 5000); // Refresh every 5 seconds (increased to reduce load)
    }

    // Cleanup interval on unmount or when autoRefresh/token changes
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, token, includeInternal]); // Include includeInternal to refresh when toggled

  const formatPrice = (price: string | number): string => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  };

  const formatQuantity = (quantity: string | number): string => {
    const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  };

  const getDataSourceBadgeClass = (isMock: boolean) => {
    return isMock 
      ? 'orderbook-badge-mock' 
      : 'orderbook-badge-real';
  };

  const getDataSourceLabel = (isMock: boolean, dataSource: string) => {
    if (isMock) return 'Mock Data';
    if (dataSource.includes('AGGREGATED')) return 'Aggregated';
    return dataSource;
  };

  return (
    <div className="orderbook-container">
      {/* Header Controls */}
      <div className="orderbook-header">
        <div className="orderbook-controls">
          <div className="orderbook-control-group">
            <label>Symbol:</label>
            <select 
              value={symbol} 
              onChange={(e) => {
                setSymbol(e.target.value);
                if (onSymbolChange) {
                  onSymbolChange(e.target.value);
                }
              }}
              className="orderbook-select"
            >
              {supportedSymbols.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="orderbook-control-group">
            <label>Provider:</label>
            <select 
              value={provider} 
              onChange={(e) => setProvider(e.target.value)}
              className="orderbook-select"
            >
              <option value="aggregated">Aggregated</option>
              <option value="KUCOIN">KuCoin</option>
              <option value="MOCK">Mock Data</option>
            </select>
          </div>

          <div className="orderbook-control-group">
            <label>Depth:</label>
            <select 
              value={depth} 
              onChange={(e) => setDepth(parseInt(e.target.value))}
              className="orderbook-select"
            >
              {depthOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="orderbook-control-group">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Auto Refresh
            </label>
          </div>

          <div className="orderbook-control-group">
            <label>
              <input
                type="checkbox"
                checked={includeInternal}
                onChange={(e) => setIncludeInternal(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Show Internal Orders
            </label>
          </div>

          <button
            onClick={loadOrderBook}
            disabled={loading}
            className="orderbook-refresh-btn"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          
          <button
            onClick={() => setShowOrderForm(!showOrderForm)}
            className="orderbook-submit-order-btn"
          >
            Place Order
          </button>
        </div>

        {/* Data Source Indicator */}
        {orderBook && (
          <div className="orderbook-data-source">
            <span className={`orderbook-badge ${getDataSourceBadgeClass(orderBook.isMockData)}`}>
              {getDataSourceLabel(orderBook.isMockData, orderBook.dataSource)}
            </span>
            {orderBook.isMockData && (
              <span className="orderbook-warning" title="This is simulated data for testing">
                ⚠️ Mock Data
              </span>
            )}
            {orderBook.hasInternalOrders && (
              <span className="orderbook-badge internal-badge-header" title={`${orderBook.internalOrderCount || 0} internal matching engine orders`}>
                🔵 Internal Orders ({orderBook.internalOrderCount || 0})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="orderbook-error">
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && !orderBook && (
        <div className="orderbook-loading">
          <div className="spinner"></div>
          <p>Loading order book...</p>
        </div>
      )}

      {/* Order Submission Form Modal */}
      {showOrderForm && (
        <div className="orderbook-modal-overlay" onClick={() => setShowOrderForm(false)}>
          <div className="orderbook-modal-content" onClick={(e) => e.stopPropagation()}>
            <OrderSubmissionForm
              symbol={symbol}
              onOrderSubmitted={() => {
                setShowOrderForm(false);
                loadOrderBook();
              }}
              onClose={() => setShowOrderForm(false)}
            />
          </div>
        </div>
      )}

      {/* Order Book Display */}
      {orderBook && !loading && (
        <div className="orderbook-content">
          {/* Spread Info */}
          <div className="orderbook-spread">
            <div className="spread-item">
              <span className="spread-label">Best Bid:</span>
              <span className="spread-value bid">{formatPrice(orderBook.bestBid)}</span>
            </div>
            <div className="spread-item">
              <span className="spread-label">Best Ask:</span>
              <span className="spread-value ask">{formatPrice(orderBook.bestAsk)}</span>
            </div>
            <div className="spread-item">
              <span className="spread-label">Spread:</span>
              <span className="spread-value">{formatPrice(orderBook.spread)}</span>
            </div>
            <div className="spread-item">
              <span className="spread-label">Spread %:</span>
              <span className="spread-value">{parseFloat(orderBook.spreadPercentage).toFixed(4)}%</span>
            </div>
          </div>

          {/* Order Book Table */}
          <div className="orderbook-table-container">
            <div className="orderbook-table">
              {/* Bids Column */}
              <div className="orderbook-column bids-column">
                <div className="orderbook-column-header">
                  <h3>Bids (Buy Orders)</h3>
                </div>
                <div className="orderbook-entries">
                  {orderBook.bids.map((bid, index) => {
                    const totalBidVolume = orderBook.bids
                      .slice(0, index + 1)
                      .reduce((sum, b) => sum + parseFloat(b.quantity), 0);
                    const maxVolume = Math.max(
                      ...orderBook.bids.map(b => parseFloat(b.quantity)),
                      ...orderBook.asks.map(a => parseFloat(a.quantity))
                    );
                    const volumePercent = (parseFloat(bid.quantity) / maxVolume) * 100;
                    const isInternal = bid.providers?.includes('INTERNAL') || false;

                    return (
                      <div key={index} className={`orderbook-entry bid-entry ${isInternal ? 'internal-order' : ''}`}>
                        <div 
                          className="volume-bar bid-bar"
                          style={{ width: `${volumePercent}%` }}
                        />
                        <div className="entry-content">
                          <span className="entry-price bid-price">{formatPrice(bid.price)}</span>
                          <span className="entry-quantity">{formatQuantity(bid.quantity)}</span>
                          <span className="entry-total">{formatQuantity(totalBidVolume)}</span>
                          {isInternal && (
                            <span className="internal-badge" title="Internal Matching Engine Order">INT</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Asks Column */}
              <div className="orderbook-column asks-column">
                <div className="orderbook-column-header">
                  <h3>Asks (Sell Orders)</h3>
                </div>
                <div className="orderbook-entries">
                  {orderBook.asks.map((ask, index) => {
                    const totalAskVolume = orderBook.asks
                      .slice(0, index + 1)
                      .reduce((sum, a) => sum + parseFloat(a.quantity), 0);
                    const maxVolume = Math.max(
                      ...orderBook.bids.map(b => parseFloat(b.quantity)),
                      ...orderBook.asks.map(a => parseFloat(a.quantity))
                    );
                    const volumePercent = (parseFloat(ask.quantity) / maxVolume) * 100;
                    const isInternal = ask.providers?.includes('INTERNAL') || false;

                    return (
                      <div key={index} className={`orderbook-entry ask-entry ${isInternal ? 'internal-order' : ''}`}>
                        <div 
                          className="volume-bar ask-bar"
                          style={{ width: `${volumePercent}%` }}
                        />
                        <div className="entry-content">
                          <span className="entry-price ask-price">{formatPrice(ask.price)}</span>
                          <span className="entry-quantity">{formatQuantity(ask.quantity)}</span>
                          <span className="entry-total">{formatQuantity(totalAskVolume)}</span>
                          {isInternal && (
                            <span className="internal-badge" title="Internal Matching Engine Order">INT</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderBook;

